import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';
import { API_CONFIG } from '../../config/api';
import { TopicDTOAdmin } from '../../types/topic';
import { getHeaders, rejectNews } from '../../utils/api';
import './FcmManagement.css';

interface ConfirmAction {
  type: 'send' | 'newsNotification';
  title: string;
  newsId?: number;
  notificationType?: 'topic' | 'country' | 'global';
}

interface NewsDTO {
  id: number;
  mainTopic: string;
  additionalTopicsEn: string | string[];
  additionalTopicsDe: string | string[];
  title: string;
  summary: string;
  imageUrl: string;
  link: string;
  language: string;
  translatedTitle: string | null;
  translatedSummary: string | null;
  state: string;
  publishedAt: string;
  importance: number;
}



const FcmManagement = () => {
  const [activeTab, setActiveTab] = useState<'fcm' | 'newsFcm'>('newsFcm');
  // FCM state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sendToTopic, setSendToTopic] = useState(false);
  const [sendToToken, setSendToToken] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [token, setToken] = useState('');
  const [topics, setTopics] = useState<TopicDTOAdmin[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<TopicDTOAdmin[]>([]);
  const [topicSearch, setTopicSearch] = useState('');
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // News FCM state
  const [news, setNews] = useState<NewsDTO[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState<string | null>(null);
  const newsState = 'APPROVED';
  const [selectedNewsTopic, setSelectedNewsTopic] = useState<string>('');
  const [selectedImportance, setSelectedImportance] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [processingCountryNotifications, setProcessingCountryNotifications] = useState<Set<number>>(new Set());
  const [processingTwitterPosts, setProcessingTwitterPosts] = useState<Set<number>>(new Set());
  const [postedToTwitter, setPostedToTwitter] = useState<Set<number>>(new Set());
  const [rejectingNewsIds, setRejectingNewsIds] = useState<Set<number>>(new Set());
  const [syncingLatestNews, setSyncingLatestNews] = useState(false);

  // News by importance for right-side horizontal cards
  const [newsImportance8_9_10, setNewsImportance8_9_10] = useState<NewsDTO[]>([]);
  const [newsImportance6, setNewsImportance6] = useState<NewsDTO[]>([]);
  const [newsImportance5, setNewsImportance5] = useState<NewsDTO[]>([]);
  const [newsImportance4, setNewsImportance4] = useState<NewsDTO[]>([]);
  const [importanceCardsLoading, setImportanceCardsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'fcm') {
      fetchTopics();
    } else {
      fetchNews();
    }
  }, [activeTab, newsState, selectedNewsTopic, selectedImportance]);

  useEffect(() => {
    if (activeTab === 'newsFcm') {
      fetchNewsByImportance();
    }
  }, [activeTab]);

  // Add country change listener
  useEffect(() => {
    const handleCountryChange = () => {
      if (activeTab === 'newsFcm') {
        fetchNews();
      }
    };

    window.addEventListener('countryChanged', handleCountryChange);
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange);
    };
  }, [activeTab]);

  const fetchTopics = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TOPICS.BASE}`);
      if (!response.ok) {
        throw new Error('Failed to fetch topics');
      }
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setTopics(data);
        setFilteredTopics(data);
      } else if (data.topics && Array.isArray(data.topics)) {
        setTopics(data.topics);
        setFilteredTopics(data.topics);
      } else if (data.data && Array.isArray(data.data)) {
        setTopics(data.data);
        setFilteredTopics(data.data);
      } else {
        console.error('Unexpected response format:', data);
        setError('Invalid response format from server');
      }
    } catch (err) {
      setError('Failed to fetch topics');
      console.error('Error:', err);
    }
  };

  const fetchNews = async () => {
    setNewsLoading(true);
    setNewsError(null);
    try {
      const params = new URLSearchParams();
      
      if (newsState) {
        params.append('state', newsState);
      }
      
      if (selectedNewsTopic) {
        params.append('topic', selectedNewsTopic);
      }

      if (selectedImportance) {
        params.append('importance', selectedImportance);
      }

      console.log('Fetching news with params:', params.toString());
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/admin/news?${params.toString()}`,
        { headers: getHeaders() }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch news: ${response.status}`);
      }

      const data: NewsDTO[] = await response.json();
      console.log('Fetched news data:', data);
      setNews(data);
    } catch (err) {
      console.error('Error in fetchNews:', err);
      setNewsError('Failed to fetch news');
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchNewsByImportance = async () => {
    setImportanceCardsLoading(true);
    try {
      const [res8, res9, res10, res6, res5, res4] = await Promise.all([
        fetch(
          `${API_CONFIG.BASE_URL}/admin/news?state=APPROVED&importance=8`,
          { headers: getHeaders() }
        ),
        fetch(
          `${API_CONFIG.BASE_URL}/admin/news?state=APPROVED&importance=9`,
          { headers: getHeaders() }
        ),
        fetch(
          `${API_CONFIG.BASE_URL}/admin/news?state=APPROVED&importance=10`,
          { headers: getHeaders() }
        ),
        fetch(
          `${API_CONFIG.BASE_URL}/admin/news?state=APPROVED&importance=6`,
          { headers: getHeaders() }
        ),
        fetch(
          `${API_CONFIG.BASE_URL}/admin/news?state=APPROVED&importance=5`,
          { headers: getHeaders() }
        ),
        fetch(
          `${API_CONFIG.BASE_URL}/admin/news?state=APPROVED&importance=4`,
          { headers: getHeaders() }
        ),
      ]);
      const data8: NewsDTO[] = res8.ok ? await res8.json() : [];
      const data9: NewsDTO[] = res9.ok ? await res9.json() : [];
      const data10: NewsDTO[] = res10.ok ? await res10.json() : [];
      setNewsImportance8_9_10([...data8, ...data9, ...data10]);
      if (res6.ok) {
        const data6: NewsDTO[] = await res6.json();
        setNewsImportance6(data6);
      } else {
        setNewsImportance6([]);
      }
      if (res5.ok) {
        const data5: NewsDTO[] = await res5.json();
        setNewsImportance5(data5);
      } else {
        setNewsImportance5([]);
      }
      if (res4.ok) {
        const data4: NewsDTO[] = await res4.json();
        setNewsImportance4(data4);
      } else {
        setNewsImportance4([]);
      }
    } catch (err) {
      console.error('Error fetching news by importance:', err);
      setNewsImportance8_9_10([]);
      setNewsImportance6([]);
      setNewsImportance5([]);
      setNewsImportance4([]);
    } finally {
      setImportanceCardsLoading(false);
    }
  };

  const QUICK_TOPICS = ['Weather', 'Accident', 'Politics', 'Autobahn', 'Strike', 'Traffic', 'Economy', 'Sports', 'Crime', 'Health'];

  const searchWithQuery = async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      await fetchNews();
      return;
    }
    setIsSearching(true);
    setNewsError(null);
    try {
      const params = new URLSearchParams({ query: trimmed });
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/admin/news/search?${params.toString()}`,
        { headers: getHeaders() }
      );
      if (!response.ok) {
        throw new Error(`Failed to search news: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setNews(data);
      } else if (data.news && Array.isArray(data.news)) {
        setNews(data.news);
      } else {
        setNewsError('Invalid response format from search');
        setNews([]);
      }
    } catch (err) {
      console.error('Error in search:', err);
      setNewsError('Failed to search news');
      setNews([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async () => {
    await searchWithQuery(searchQuery);
  };

  const handleSyncLatestNews = async () => {
    setSyncingLatestNews(true);
    setNewsError(null);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RSS.SYNC_LATEST_NEWS}`,
        {
          method: 'POST',
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to sync latest news: ${response.status}`);
      }

      const data = await response.json();
      setSuccess(data.message || 'Latest news synced successfully!');
      await Promise.all([
        searchQuery.trim() ? searchWithQuery(searchQuery) : fetchNews(),
        fetchNewsByImportance(),
      ]);
    } catch (err) {
      console.error('Error syncing latest news:', err);
      setNewsError('Failed to sync latest news');
    } finally {
      setSyncingLatestNews(false);
    }
  };

  const handleTopicClick = (topic: string) => {
    setSearchQuery(topic);
    searchWithQuery(topic);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    // Filter topics based on search query
    if (topicSearch) {
      const filtered = topics.filter(topic =>
        topic.name.toLowerCase().includes(topicSearch.toLowerCase())
      );
      setFilteredTopics(filtered);
    } else {
      setFilteredTopics(topics);
    }
  }, [topicSearch, topics]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTopicDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setImageUrl('');
    }
  };

  const handleCheckboxChange = (type: 'topic' | 'token') => {
    if (type === 'topic') {
      setSendToTopic(!sendToTopic);
      setSendToToken(false);
    } else {
      setSendToToken(!sendToToken);
      setSendToTopic(false);
    }
  };

  const handleTopicSelect = (topic: TopicDTOAdmin) => {
    setSelectedTopic(topic.name);
    setShowTopicDropdown(false);
    setTopicSearch('');
  };

  const handleTopicSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTopicSearch(e.target.value);
    setShowTopicDropdown(true);
  };

  const uploadImage = async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.UPLOAD_IMAGE}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    } catch (err) {
      throw new Error('Failed to upload image');
    }
  };

  const handleSendClick = () => {
    setConfirmAction({
      type: 'send',
      title: title
    });
  };

  const sendNotification = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let finalImageUrl = imageUrl;
      
      if (selectedFile) {
        finalImageUrl = await uploadImage(selectedFile);
      }

      const notificationData = {
        title,
        description,
        imageUrl: finalImageUrl,
        ...(sendToTopic && { topic: selectedTopic }),
        ...(sendToToken && { token }),
      };

      const endpoint = sendToTopic 
        ? `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.SEND_TO_TOPIC}`
        : `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.SEND_TO_TOKEN}`;

      await axios.post(endpoint, notificationData);
      setSuccess('Notification sent successfully!');
      
      // Reset form
      setTitle('');
      setDescription('');
      setImageUrl('');
      setSelectedFile(null);
      setSendToTopic(false);
      setSendToToken(false);
      setSelectedTopic('');
      setToken('');
    } catch (err) {
      setError('Failed to send notification');
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  const handleSendNewsNotification = async (newsId: number, type: 'topic' | 'country' | 'global') => {
    if (type === 'country') {
      // For country notifications, immediately remove the news item and send notification in background
      await handleCountryNotification(newsId);
    } else {
      // For other notification types, show confirmation dialog
      setConfirmAction({
        type: 'newsNotification',
        title: `Send ${type.charAt(0).toUpperCase() + type.slice(1)} Notification`,
        newsId,
        notificationType: type
      });
    }
  };

  const handleCountryNotification = async (newsId: number) => {
    // Add to processing set
    setProcessingCountryNotifications(prev => new Set(prev).add(newsId));
    
    try {
      // Immediately remove the news item from all card lists
      removeNewsFromAllLists(newsId);
      
      // Send notification in background
      const notificationData = {
        newsId: newsId,
        type: 'COUNTRY'
      };

      // Fire and forget - don't await this
      axios.post(
        `${API_CONFIG.BASE_URL}/api/firebase/SendNotificationToNewsId`,
        notificationData
      ).catch(err => {
        console.error('Background country notification failed:', err);
        // Optionally show a subtle error message or log it
      });
      
    } catch (err) {
      console.error('Error in handleCountryNotification:', err);
      // If there's an error, we could optionally restore the news item
      // For now, we'll just log the error
    } finally {
      // Remove from processing set
      setProcessingCountryNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(newsId);
        return newSet;
      });
    }
  };

  const removeNewsFromAllLists = (newsId: number) => {
    setNews(prev => prev.filter(item => item.id !== newsId));
    setNewsImportance8_9_10(prev => prev.filter(item => item.id !== newsId));
    setNewsImportance6(prev => prev.filter(item => item.id !== newsId));
    setNewsImportance5(prev => prev.filter(item => item.id !== newsId));
    setNewsImportance4(prev => prev.filter(item => item.id !== newsId));
  };

  const findNewsById = (newsId: number): NewsDTO | undefined => {
    return news.find(item => item.id === newsId)
      ?? newsImportance8_9_10.find(item => item.id === newsId)
      ?? newsImportance6.find(item => item.id === newsId)
      ?? newsImportance5.find(item => item.id === newsId)
      ?? newsImportance4.find(item => item.id === newsId);
  };

  const handleRejectNews = async (newsId: number, event?: React.MouseEvent) => {
    if (event) event.preventDefault();
    setRejectingNewsIds(prev => new Set(prev).add(newsId));
    removeNewsFromAllLists(newsId);
    try {
      await rejectNews(newsId);
    } catch (err) {
      console.error('Reject news failed:', err);
    } finally {
      setRejectingNewsIds(prev => {
        const next = new Set(prev);
        next.delete(newsId);
        return next;
      });
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'newsNotification' && confirmAction.newsId && confirmAction.notificationType) {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const notificationData = {
          newsId: confirmAction.newsId,
          type: confirmAction.notificationType.toUpperCase()
        };

        await axios.post(
          `${API_CONFIG.BASE_URL}/api/firebase/SendNotificationToNewsId`,
          notificationData
        );

        setSuccess('News notification sent successfully!');
        // Refresh news after successful notification
        await fetchNews();
      } catch (err) {
        setError('Failed to send news notification');
        console.error('Error sending notification:', err);
      } finally {
        setLoading(false);
        setConfirmAction(null);
      }
    } else if (confirmAction.type === 'send') {
      await sendNotification();
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    let interval = seconds / 31536000; // years
    if (interval > 1) return Math.floor(interval) + ' years ago';
    
    interval = seconds / 2592000; // months
    if (interval > 1) return Math.floor(interval) + ' months ago';
    
    interval = seconds / 86400; // days
    if (interval > 1) return Math.floor(interval) + ' days ago';
    
    interval = seconds / 3600; // hours
    if (interval > 1) return Math.floor(interval) + ' hours ago';
    
    interval = seconds / 60; // minutes
    if (interval > 1) return Math.floor(interval) + ' minutes ago';
    
    return Math.floor(seconds) + ' seconds ago';
  };

  const handlePostToTwitter = async (newsId: number, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    
    // Add to processing set
    setProcessingTwitterPosts(prev => new Set(prev).add(newsId));
    
    try {
      const newsItem = findNewsById(newsId);
      if (!newsItem) {
        throw new Error('News item not found');
      }

      const notificationData = {
        title: newsItem.title,
        description: newsItem.summary,
        imageUrl: newsItem.imageUrl,
        topic: null,
        token: null,
        newsId: newsId
      };

      // Fire and forget - don't await this
      fetch(
        `${API_CONFIG.BASE_URL}/api/firebase/postToTwitter`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(notificationData)
        }
      ).then(response => {
        if (!response.ok) {
          throw new Error('Failed to post to Twitter');
        }
        console.log('Posted to Twitter successfully for news ID:', newsId);
        // Add to posted set to permanently disable the button
        setPostedToTwitter(prev => new Set(prev).add(newsId));
      }).catch(err => {
        console.error('Background Twitter post failed:', err);
        // Optionally show a subtle error message or log it
      });
      
    } catch (err) {
      console.error('Error in handlePostToTwitter:', err);
      // If there's an error, we could optionally show a subtle error message
      // For now, we'll just log the error
    } finally {
      // Remove from processing set
      setProcessingTwitterPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(newsId);
        return newSet;
      });
    }
  };

  return (
    <div className="fcm-management">
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'fcm' ? 'active' : ''}`}
          onClick={() => setActiveTab('fcm')}
        >
          FCM
        </button>
        <button 
          className={`tab ${activeTab === 'newsFcm' ? 'active' : ''}`}
          onClick={() => setActiveTab('newsFcm')}
        >
          News FCM
        </button>
      </div>

      {activeTab === 'fcm' ? (
        // Existing FCM form
        <>
          <h2>FCM Notification Management</h2>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label>Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Description:</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Image:</label>
            <div className="image-options-column">
              <div className="image-option">
                <input
                  type="text"
                  placeholder="Image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  disabled={!!selectedFile}
                />
              </div>
              <div className="image-option">
                <span>OR</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={!!imageUrl}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={sendToTopic}
                  onChange={() => handleCheckboxChange('topic')}
                />
                Send to Topic
              </label>
              {sendToTopic && (
                <div className="topic-select-container" ref={dropdownRef}>
                  <input
                    type="text"
                    value={topicSearch}
                    onChange={handleTopicSearchChange}
                    onFocus={() => setShowTopicDropdown(true)}
                    placeholder="Search topics..."
                    className="topic-search-input"
                  />
                  {showTopicDropdown && (
                    <div className="topic-dropdown-list">
                      {filteredTopics.length > 0 ? (
                        filteredTopics.map((topic) => (
                          <div
                            key={topic.id}
                            className="topic-dropdown-item"
                            onClick={() => handleTopicSelect(topic)}
                          >
                            {topic.name}
                          </div>
                        ))
                      ) : (
                        <div className="topic-dropdown-item no-results">No topics found</div>
                      )}
                    </div>
                  )}
                  {selectedTopic && (
                    <div className="selected-topic">
                      Selected: {selectedTopic}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={sendToToken}
                  onChange={() => handleCheckboxChange('token')}
                />
                Send to Token
              </label>
              {sendToToken && (
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter FCM token"
                  required
                />
              )}
            </div>
          </div>

          <button
            onClick={handleSendClick}
            disabled={loading || (!sendToTopic && !sendToToken)}
            className="send-button"
          >
            {loading ? 'Sending...' : 'Send Notification'}
          </button>
        </>
      ) : (
        // News FCM section
        <>
          <h2>News FCM Management</h2>
          
          {newsError && <div className="error-message">{newsError}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="sync-latest-news-bar">
            <button
              type="button"
              className="sync-latest-news-button"
              onClick={handleSyncLatestNews}
              disabled={syncingLatestNews}
            >
              {syncingLatestNews ? 'Syncing latest news...' : 'Sync latest news'}
            </button>
          </div>

          <div className="quick-topics">
            {QUICK_TOPICS.map((topic) => (
              <button
                key={topic}
                type="button"
                className={`quick-topic-chip ${searchQuery === topic ? 'active' : ''}`}
                onClick={() => handleTopicClick(topic)}
                disabled={isSearching}
              >
                {topic}
              </button>
            ))}
          </div>

          <div className="search-container">
            <div className="search-input-group">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="Search news by title, summary, or content..."
                className="search-input"
              />
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="search-button"
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  fetchNews();
                }}
                className="clear-search-button"
              >
                Clear Search
              </button>
            )}
          </div>

          <div className="news-filters">
            <select
              value={selectedNewsTopic}
              onChange={(e) => setSelectedNewsTopic(e.target.value)}
              className="filter-select"
            >
              <option value="">All Topics</option>
              {Array.from(new Set(news.map(item => item.mainTopic))).map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>

            <select
              value={selectedImportance}
              onChange={(e) => setSelectedImportance(e.target.value)}
              className="filter-select"
            >
              <option value="">All Importance Levels</option>
              {[...Array(11)].map((_, index) => (
                <option key={index} value={index}>
                  Importance: {index}
                </option>
              ))}
            </select>
          </div>

          {newsLoading ? (
            <div className="loading">Loading news...</div>
          ) : (
            <div className="news-fcm-layout">
              <div className="news-cards-column">
                <div className="importance-card">
                  <h3 className="importance-card-title">News</h3>
                  <div className="importance-card-scroll">
                    {news.length === 0 ? (
                      <p className="importance-card-empty">No news</p>
                    ) : (
                      news.map(item => (
                        <div key={item.id} className="news-card-horizontal">
                          <div className="news-card-h-image">
                            <img src={item.imageUrl} alt={item.title} />
                          </div>
                          <div className="news-card-h-content">
                            <h4>{item.title}</h4>
                            <div className="news-card-h-actions-top">
                              <button 
                                onClick={(e) => handleSendNewsNotification(item.id, 'country')}
                                className="action-button country-button"
                                disabled={processingCountryNotifications.has(item.id)}
                              >
                                {processingCountryNotifications.has(item.id) ? '...' : 'Send notification'}
                              </button>
                              <button 
                                onClick={(e) => handlePostToTwitter(item.id, e)}
                                className="action-button twitter-button"
                                disabled={processingTwitterPosts.has(item.id) || postedToTwitter.has(item.id)}
                              >
                                {postedToTwitter.has(item.id) ? 'Posted' : 'Post to Twitter'}
                              </button>
                            </div>
                            <p className="news-card-h-summary">{item.summary}</p>
                            <div className="news-card-h-meta">{getTimeAgo(item.publishedAt)} · Importance: {item.importance}</div>
                            <div className="news-card-h-actions-bottom">
                              <button 
                                onClick={(e) => handleRejectNews(item.id, e)}
                                className="action-button reject-button"
                                disabled={rejectingNewsIds.has(item.id)}
                              >
                                {rejectingNewsIds.has(item.id) ? '...' : 'Reject'}
                              </button>
                              <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button read-button">Read</a>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                {(importanceCardsLoading || newsImportance8_9_10.length > 0) && (
                  <div className="importance-card">
                    <h3 className="importance-card-title">Importance 8, 9 & 10</h3>
                    {importanceCardsLoading ? (
                      <div className="loading loading-sm">Loading...</div>
                    ) : (
                      <div className="importance-card-scroll">
                        {newsImportance8_9_10.map(item => (
                          <div key={item.id} className="news-card-horizontal">
                            <div className="news-card-h-image">
                              <img src={item.imageUrl} alt={item.title} />
                            </div>
                            <div className="news-card-h-content">
                              <h4>{item.title}</h4>
                              <div className="news-card-h-actions-top">
                                <button 
                                  onClick={(e) => handleSendNewsNotification(item.id, 'country')}
                                  className="action-button country-button"
                                  disabled={processingCountryNotifications.has(item.id)}
                                >
                                  {processingCountryNotifications.has(item.id) ? '...' : 'Send notification'}
                                </button>
                                <button 
                                  onClick={(e) => handlePostToTwitter(item.id, e)}
                                  className="action-button twitter-button"
                                  disabled={processingTwitterPosts.has(item.id) || postedToTwitter.has(item.id)}
                                >
                                  {postedToTwitter.has(item.id) ? 'Posted' : 'Post to Twitter'}
                                </button>
                              </div>
                              <p className="news-card-h-summary">{item.summary}</p>
                              <div className="news-card-h-meta">{getTimeAgo(item.publishedAt)} · Importance: {item.importance}</div>
                              <div className="news-card-h-actions-bottom">
                                <button 
                                  onClick={(e) => handleRejectNews(item.id, e)}
                                  className="action-button reject-button"
                                  disabled={rejectingNewsIds.has(item.id)}
                                >
                                  {rejectingNewsIds.has(item.id) ? '...' : 'Reject'}
                                </button>
                                <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button read-button">Read</a>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="importance-card">
                  <h3 className="importance-card-title">Importance 6</h3>
                  {importanceCardsLoading ? (
                    <div className="loading loading-sm">Loading...</div>
                  ) : (
                    <div className="importance-card-scroll">
                      {newsImportance6.length === 0 ? (
                        <p className="importance-card-empty">No news with importance 6</p>
                      ) : (
                        newsImportance6.map(item => (
                          <div key={item.id} className="news-card-horizontal">
                            <div className="news-card-h-image">
                              <img src={item.imageUrl} alt={item.title} />
                            </div>
                            <div className="news-card-h-content">
                              <h4>{item.title}</h4>
                              <div className="news-card-h-actions-top">
                                <button 
                                  onClick={(e) => handleSendNewsNotification(item.id, 'country')}
                                  className="action-button country-button"
                                  disabled={processingCountryNotifications.has(item.id)}
                                >
                                  {processingCountryNotifications.has(item.id) ? '...' : 'Send notification'}
                                </button>
                                <button 
                                  onClick={(e) => handlePostToTwitter(item.id, e)}
                                  className="action-button twitter-button"
                                  disabled={processingTwitterPosts.has(item.id) || postedToTwitter.has(item.id)}
                                >
                                  {postedToTwitter.has(item.id) ? 'Posted' : 'Post to Twitter'}
                                </button>
                              </div>
                              <p className="news-card-h-summary">{item.summary}</p>
                              <div className="news-card-h-meta">{getTimeAgo(item.publishedAt)}</div>
                              <div className="news-card-h-actions-bottom">
                                <button 
                                  onClick={(e) => handleRejectNews(item.id, e)}
                                  className="action-button reject-button"
                                  disabled={rejectingNewsIds.has(item.id)}
                                >
                                  {rejectingNewsIds.has(item.id) ? '...' : 'Reject'}
                                </button>
                                <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button read-button">Read</a>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="importance-card">
                  <h3 className="importance-card-title">Importance 5</h3>
                  {importanceCardsLoading ? (
                    <div className="loading loading-sm">Loading...</div>
                  ) : (
                    <div className="importance-card-scroll">
                      {newsImportance5.length === 0 ? (
                        <p className="importance-card-empty">No news with importance 5</p>
                      ) : (
                        newsImportance5.map(item => (
                          <div key={item.id} className="news-card-horizontal">
                            <div className="news-card-h-image">
                              <img src={item.imageUrl} alt={item.title} />
                            </div>
                            <div className="news-card-h-content">
                              <h4>{item.title}</h4>
                              <div className="news-card-h-actions-top">
                                <button 
                                  onClick={(e) => handleSendNewsNotification(item.id, 'country')}
                                  className="action-button country-button"
                                  disabled={processingCountryNotifications.has(item.id)}
                                >
                                  {processingCountryNotifications.has(item.id) ? '...' : 'Send notification'}
                                </button>
                                <button 
                                  onClick={(e) => handlePostToTwitter(item.id, e)}
                                  className="action-button twitter-button"
                                  disabled={processingTwitterPosts.has(item.id) || postedToTwitter.has(item.id)}
                                >
                                  {postedToTwitter.has(item.id) ? 'Posted' : 'Post to Twitter'}
                                </button>
                              </div>
                              <p className="news-card-h-summary">{item.summary}</p>
                              <div className="news-card-h-meta">{getTimeAgo(item.publishedAt)}</div>
                              <div className="news-card-h-actions-bottom">
                                <button 
                                  onClick={(e) => handleRejectNews(item.id, e)}
                                  className="action-button reject-button"
                                  disabled={rejectingNewsIds.has(item.id)}
                                >
                                  {rejectingNewsIds.has(item.id) ? '...' : 'Reject'}
                                </button>
                                <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button read-button">Read</a>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="importance-card">
                  <h3 className="importance-card-title">Importance 4</h3>
                  {importanceCardsLoading ? (
                    <div className="loading loading-sm">Loading...</div>
                  ) : (
                    <div className="importance-card-scroll">
                      {newsImportance4.length === 0 ? (
                        <p className="importance-card-empty">No news with importance 4</p>
                      ) : (
                        newsImportance4.map(item => (
                          <div key={item.id} className="news-card-horizontal">
                            <div className="news-card-h-image">
                              <img src={item.imageUrl} alt={item.title} />
                            </div>
                            <div className="news-card-h-content">
                              <h4>{item.title}</h4>
                              <div className="news-card-h-actions-top">
                                <button 
                                  onClick={(e) => handleSendNewsNotification(item.id, 'country')}
                                  className="action-button country-button"
                                  disabled={processingCountryNotifications.has(item.id)}
                                >
                                  {processingCountryNotifications.has(item.id) ? '...' : 'Send notification'}
                                </button>
                                <button 
                                  onClick={(e) => handlePostToTwitter(item.id, e)}
                                  className="action-button twitter-button"
                                  disabled={processingTwitterPosts.has(item.id) || postedToTwitter.has(item.id)}
                                >
                                  {postedToTwitter.has(item.id) ? 'Posted' : 'Post to Twitter'}
                                </button>
                              </div>
                              <p className="news-card-h-summary">{item.summary}</p>
                              <div className="news-card-h-meta">{getTimeAgo(item.publishedAt)}</div>
                              <div className="news-card-h-actions-bottom">
                                <button 
                                  onClick={(e) => handleRejectNews(item.id, e)}
                                  className="action-button reject-button"
                                  disabled={rejectingNewsIds.has(item.id)}
                                >
                                  {rejectingNewsIds.has(item.id) ? '...' : 'Reject'}
                                </button>
                                <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button read-button">Read</a>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {confirmAction && (
        <ConfirmDialog
          isOpen={!!confirmAction}
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleConfirmAction}
          title={confirmAction.type === 'newsNotification' ? 'Confirm Send Notification' : 'Confirm Send Notification'}
          message={
            confirmAction.type === 'newsNotification'
              ? `Are you sure you want to send a ${confirmAction.notificationType} notification for this news?`
              : `Are you sure you want to send the notification "${confirmAction.title}"?`
          }
          confirmText="Send"
          cancelText="Cancel"
          type="approve"
        />
      )}
    </div>
  );
};

export default FcmManagement; 
